import type { Ref } from 'vue';
import { computed } from 'vue';

import type { IContextQueryResult } from '@modules/core/services/query/types';
import type { ISmartFilterColumnData } from '@modules/smart-filters/types';
import { useRestClient } from '@modules/core/services/api';
import { useContextQuery } from '@modules/core/services/query';

import type {
  ITableFilterGetColumnPayload,
} from '../types';
import {
  TABLE_FILTER_COLUMN_QUERY_KEY,
  TABLE_FILTER_COLUMN_URL,
  TABLES_PERMISSIONS,
} from '../constants';

export default function useTableFilterColumnQuery<T extends string = string>(
  payload: Ref<ITableFilterGetColumnPayload<T>>,
  enabled: Ref<boolean>,
): IContextQueryResult<Record<T, ISmartFilterColumnData>, Error, Record<T, ISmartFilterColumnData>[T] | undefined> {
  const { fetchGet } = useRestClient();

  return useContextQuery<Record<T, ISmartFilterColumnData>, Error, Record<T, ISmartFilterColumnData>[T] | undefined>({
    queryKey: [TABLE_FILTER_COLUMN_QUERY_KEY, payload],
    queryFn: ({ signal }) => {
      const { filterId, columnName, ...queryParams } = payload.value;

      return fetchGet(
        TABLE_FILTER_COLUMN_URL,
        {
          queryParams,
          urlParams: {
            filterId,
            columnName,
          },
          signal,
          permission: TABLES_PERMISSIONS.FILTERSETS_SHOW,
        },
      );
    },
    select: (data) => (
      payload.value.columnName ? data[payload.value.columnName] : undefined
    ),
    gcTime: 1000,
    enabled: computed(() => enabled.value && !!payload.value.filterId && !!payload.value.columnName),
  });
}
